import {
  resolvePositionAssignments,
  type ActivatedUserPayload,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { DEFAULT_ICS_FORM_FAMILY } from 'components/IAP/formFamily';
import {
  defaultDraftSeats,
  rosterNodesForCreate,
} from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import type { Incident } from 'components/IAP/hooks/incidentHooks';
import {
  focInitialResponseDemoNeedsOaSeed,
  seedFocInitialResponseDemoObjectivesActions,
} from '../focInitialResponseTour/focInitialResponseDemo';
import { INITIAL_RESPONSE_VIEW_PARAM } from 'utils/initialResponseView';

/** Stable Hub name for the FOC 9.1 Transition demo (escalated during the tour). */
export const FOC_DEMO_TRANSITION_NAME = 'FOC Demo — Transition';

/** Keep in sync with FOC_TRANSITION_TOUR_PARAM (avoid circular import). */
const FOC_TRANSITION_TOUR_PARAM = 'focTransitionTour';

export type FocTransitionIncident = {
  id: string;
  name?: string | null;
  archived?: number | boolean | null;
  escalated_at?: string | null;
};

const isLive = (incident: FocTransitionIncident) => !incident.archived;

export function findFocTransitionDemo(
  incidents: readonly FocTransitionIncident[] = []
): FocTransitionIncident | null {
  const demo = incidents.find(
    (incident) =>
      isLive(incident) &&
      String(incident.name ?? '').trim() === FOC_DEMO_TRANSITION_NAME
  );
  return demo ?? null;
}

/**
 * Prefer named Transition demo (live), else first live unescalated for create
 * fallbacks when resolving hrefs.
 */
export function preferredFocTransitionId(
  incidents: readonly FocTransitionIncident[] = []
): string | null {
  const demo = findFocTransitionDemo(incidents);
  if (demo) return String(demo.id);
  return null;
}

export type EnsureFocTransitionDemoDeps = {
  incidents: readonly FocTransitionIncident[];
  groupGuid: string;
  user: {
    user_guid: string;
    name?: string | null;
    email_address?: string | null;
  };
  createIncident: (payload: {
    name: string;
    group_guid: string;
    areas_of_responsibility: string[];
    incident_types: string[];
    ics_form_family: string;
    activated_users: ActivatedUserPayload[];
    roster_nodes: ReturnType<typeof rosterNodesForCreate>;
  }) => Promise<Pick<Incident, 'id' | 'name'> & { escalated_at?: string | null }>;
};

async function seedOaIfNeeded(deps: {
  incidentId: string;
  groupGuid: string;
  userGuid: string;
}): Promise<void> {
  try {
    const needsSeed = await focInitialResponseDemoNeedsOaSeed(deps.incidentId);
    if (!needsSeed) return;
    await seedFocInitialResponseDemoObjectivesActions(deps);
  } catch {
    // Tour still opens; OA continuity steps fall back to empty-state UI.
  }
}

export type EnsureFocTransitionDemoResult = {
  incidentId: string;
  escalated: boolean;
};

/**
 * Returns the Transition demo id, creating a Training workspace (IC-seated,
 * unescalated, with OA seed) when the named demo is missing.
 */
export async function ensureFocTransitionDemoWorkspace(
  deps: EnsureFocTransitionDemoDeps
): Promise<EnsureFocTransitionDemoResult | null> {
  const existing = findFocTransitionDemo(deps.incidents);
  if (existing) {
    const incidentId = String(existing.id);
    if (deps.groupGuid && deps.user.user_guid && !existing.escalated_at) {
      await seedOaIfNeeded({
        incidentId,
        groupGuid: deps.groupGuid,
        userGuid: deps.user.user_guid,
      });
    }
    return {
      incidentId,
      escalated: Boolean(existing.escalated_at),
    };
  }

  if (!deps.groupGuid || !deps.user.user_guid) return null;

  const seats = defaultDraftSeats();
  const activatedUser: ActivatedUserPayload = {
    user_guid: deps.user.user_guid,
    role: 'Incident Commander',
    email_address: deps.user.email_address || '',
    name: deps.user.name || deps.user.email_address || 'FOC Demo User',
  };

  const created = await deps.createIncident({
    name: FOC_DEMO_TRANSITION_NAME,
    group_guid: deps.groupGuid,
    areas_of_responsibility: [],
    incident_types: ['Training'],
    ics_form_family: DEFAULT_ICS_FORM_FAMILY,
    activated_users: [activatedUser],
    roster_nodes: rosterNodesForCreate(seats),
  });

  const incidentId = created?.id ? String(created.id) : null;
  if (!incidentId) return null;

  try {
    const { addUsersToIncidentRoster, getPositionsForIncident } = await import(
      'hooks/incident/useRoster'
    );
    const positions = await getPositionsForIncident(incidentId);
    const positionAssignments = resolvePositionAssignments(
      [
        {
          user_guid: deps.user.user_guid,
          name: activatedUser.name,
          email_address: activatedUser.email_address,
          position: 'Incident Commander',
        },
      ],
      positions
    );
    if (positionAssignments.length > 0) {
      await addUsersToIncidentRoster({
        incidentId,
        groupGuid: deps.groupGuid,
        positionAssignments,
      });
    }
  } catch {
    // Creator remains an activated member.
  }

  await seedOaIfNeeded({
    incidentId,
    groupGuid: deps.groupGuid,
    userGuid: deps.user.user_guid,
  });

  return { incidentId, escalated: false };
}

export function focTransitionIrHref(incidentId: string): string {
  return `/iap/${incidentId}/initial-response?${INITIAL_RESPONSE_VIEW_PARAM}=new&${FOC_TRANSITION_TOUR_PARAM}=1`;
}

export function focTransitionPlanningHref(incidentId: string): string {
  const params = new URLSearchParams({
    mode: 'future',
    formsView: 'working',
    phase: '1',
    [FOC_TRANSITION_TOUR_PARAM]: 'planning',
  });
  return `/iap/${incidentId}/dashboard?${params.toString()}`;
}

export function focTransitionDemoHref(
  incidentId: string,
  escalated: boolean
): string {
  return escalated
    ? focTransitionPlanningHref(incidentId)
    : focTransitionIrHref(incidentId);
}
